<template>
    <div class="tasks-page-wrapper">
        <HLCardLeftLayout
            v-model="showTask"
            :autoCheckin="autoCheckin"
            :key="invalidateCounter"
            @input="onCardLayoutChanged"
            :loadingMenu="list_loading"
            :loadingMain="spot_solve_loading"
            :insetMain="inset"
            :singleView="singleView"
            :hideMenu="hideTaskList"
            :transitionKey="selectedSpot ? selectedSpot.id : 0"
            :backgroundMenu="theme.backgroundColor"
        >
            <template #menu-title>
                <div class="menu-title-row">
                    <span class="menu-title-text grey--text text--darken-3" :style="{'color':theme.footerIconColor + '!important'}">{{ labelTasks }}</span>
                    <div class="menu-title-actions" v-if="shouldPlaceActionsInTitle">
                        <HLSpotListActions
                            :compactLayout="shouldPlaceActionsInTitle"
                            :mode="mode"
                            :showQrActions="hasQrCheckin"
                            :showPwActions="hasPwCheckin"
                            @refresh="onRefreshSpotList"
                            @switchListMode="onSwitchListMode"
                            @changeSortOrder="onChangeSortOrder"
                            @qrAction="onQrAction"
                            @pwAction="onPwAction"
                        />
                    </div>
                </div>
            </template>
            <template #menu-actions>
                <HLSpotListActions
                    v-if="!shouldPlaceActionsInTitle"
                    class="pb-2"
                    :compactLayout="shouldPlaceActionsInTitle"
                    :mode="mode"
                    :showQrActions="hasQrCheckin"
                    :showPwActions="hasPwCheckin"
                    @refresh="onRefreshSpotList"
                    @switchListMode="onSwitchListMode"
                    @changeSortOrder="onChangeSortOrder"
                    @qrAction="onQrAction"
                    @pwAction="onPwAction"
                />
            </template>
            <template #menu>
                <HLSpotList
                    ref="spotList"
                    :spot="selectedSpot"
                    :mode="mode"
                    :sortOrder="sortOrder"
                    :eventId="selectedEventId"
                    @select="onSelectTaskFromList"
                />
            </template>
            <template #main-title>
            <span :class="inset ? 'white--text' : 'grey--text text--darken-3'" :style="{'color':theme.footerIconColor + '!important'}">
                {{ spotTitle }}
            </span>
            </template>
            <template #main>
                <EspAnswerAnimation
                    v-model="showAnswerAnimation"
                    :answerResultType="answerResultType"
                    :isMobil="isXsDevice"
                    class=""
                />
                <!-- Destroyed on close so embedded video/audio stops. singleView (QR, password and
                     auto-checkin spots via the taskpage store) shows the main pane without setting showTask. -->
                <HLSpotDetails
                    v-if="showTask || singleView"
                    v-show="!showAnswerAnimation"
                    id="hlSpotDetails"
                    :spot="selectedSpot"
                    :inset="inset"
                    :active="taskPanelActive"
                    :wizardStep="wizardStep"
                    :error="error"
                    class="px-0 px-sm-0"
                    @select="onOpenTask"
                    @input="onTaskInput"
                    @reloadList="reloadSpotListOnCantPlayAgain"
                />
            </template>
            <template #background>
                <EspMap
                    v-show="showmap"
                    ref="map"
                    name="map"
                    :loading="list_loading"
                    :event="selectedEvent"
                    :eventWaypoints="allActiveVisibleSpots"
                    :indoorMaps="selectedEvent.indoormaps"
                    :eventMapstyle="selectedEvent.mapstyle"
                    :hideTiles="!settingsShowMapTiles"
                    @select="onSelectTaskFromMap"
                />
            </template>
            <template #footer>
                <HLSpotDetailsFooter
                    ref="spotDetailsFooter"
                    class="pt-1 pa-0 px-1 px-sm-5 px-lg-10"
                    :spot="selectedSpot"
                    :taskInput="taskInput"
                    :error="error"
                    :active="!eventIsInactive"
                    :singleView="singleView"
                    :autoCheckin="autoCheckin"
                    :hideFooter="hideFooter"
                    :timezone="timezone"
                    @next="onGoNext"
                    @prev="onGoPrev"
                    @checkin="onCheckin"
                    @solve="onSolve"
                    @help="onHelp"
                    @requestHint="onRequestHint"
                    @close="onCloseTask"
                    @changedWizardStep="onChangedWizardStep"
                    :showNext="showNext"
                    :showPrev="showPrev"
                    :customNames="{
          labelTask: labelTask,
          labelTaskAac: labelTaskAac,
          labelTaskCheckin: labelTaskCheckin,
          labelTaskPass: labelTaskPass,
          labelTaskSolve: labelTaskSolve,
          labelStepperTaskAac:
            selectedEvent && selectedEvent.lbl_app_task_aac
              ? selectedEvent.lbl_app_task_aac
              : null,
          labelStepperTaskCheckin:
            selectedEvent && selectedEvent.lbl_app_task_checkin
              ? selectedEvent.lbl_app_task_checkin
              : null,
          labelStepperTaskSolve:
            selectedEvent && selectedEvent.lbl_app_task_solve
              ? selectedEvent.lbl_app_task_solve
              : null,
        }"
                />
            </template>

        </HLCardLeftLayout>

        <!-- QR Dialog Overlay -->
        <v-overlay :value="qrDialogOverlay" :style="`width:${bgWidth}px;`" :dark="false" :color="overlayColor" :opacity="0.95">
            <QrDialogPage
                @cancel="onQrCancel"
                @success="onQrSuccess"
                :class="qrAnimationClass"
            />
        </v-overlay>

        <!-- Password Dialog Overlay -->
        <v-overlay :value="passwordDialogOverlay" :style="`width:${bgWidth}px;`" :dark="false" :color="overlayColor" :opacity="0.95">
            <PasswordDialogPage
                @cancel="onPasswordCancel"
                @success="onPasswordSuccess"
                :class="passwordAnimationClass"
            />
        </v-overlay>

        <!-- GPS problem dialog -->
        <HLConfirm v-model="gpsDialog" v-bind="gpsDialogOptions"/>
    </div>
</template>

<script>

import CONFIG from "../../constants/config";
import {layoutMixin} from "../../utils/layout-mixin";
import {eventMixin} from "../../utils/event-mixin";
import {spotMixin} from "../../utils/spot-mixin";
import {geolocationMixin} from "../../utils/geolocation-mixin";
import {teamMixin} from "../../utils/team-mixin";
import {solutionMixin} from "../../utils/solution-mixin";
import {dateMixin} from "../../utils/date-mixin";
import {EventBus} from "../../components/spots/EventBus";
import {EventBusNotification} from "../../components/EventBusNotification";
import {isCantPlayAgainError} from "../../utils/request-error.mjs";
import {mapGetters} from 'vuex';
import HLCardLeftLayout from "../../components/dashboard/HLCardLeftLayout";
import HLSpotList from "../../components/dashboard/HLSpotList";
import HLSpotListActions from "../../components/dashboard/HLSpotListActions";
import HLSpotDetails from "../../components/dashboard/HLSpotDetails";
import HLSpotDetailsFooter from "../../components/dashboard/HLSpotDetailsFooter";
import EspAnswerAnimation from "../../components/animation/EspAnswerAnimation";
import EspMap from "../../components/map/EspMap";
import QrDialogPage from "../../views/dashboard/QrDialogPage";
import PasswordDialogPage from "../../views/dashboard/PasswordDialogPage";
import HLConfirm from "../../components/ui/HLConfirm";

export default {
    mixins: [layoutMixin, eventMixin, spotMixin, geolocationMixin, dateMixin, teamMixin, solutionMixin],
    components: {
        HLCardLeftLayout,
        HLSpotList,
        HLSpotListActions,
        HLSpotDetails,
        HLSpotDetailsFooter,
        EspAnswerAnimation,
        EspMap,
        QrDialogPage,
        PasswordDialogPage,
        HLConfirm,
    },
    props: {
    },
    data() {
        return {
            hideTaskList: false,
            showTask: false,
            // keep-alive state; a singleView spot stays mounted while another page is shown
            pageActive: true,
            selectedSpot: null,
            sortOrder: { order: 'asc', option: 'byNumber' },
            mode: 0,
            wizardStep: 0,
            taskInput: null,
            taskInputServerData: null,
            inset: false,
            error: null,
            answerResultType: null,
            showAnswerAnimation: false,
            invalidateCounter: 0,
            centerSpotImmediatly: false,
            // Dialog overlays
            qrDialogOverlay: false,
            passwordDialogOverlay: false,
            overlayColor: '#333',
            qrAnimationClass: '',
            passwordAnimationClass: '',
            animationFailedDuration: 1000,
            animationSuccessDuration: 1500
        };
    },
    watch: {
        $route: {
            handler: function (route) {
                if (route && route.name == 'map') {
                    this.setMapViewToFullscreen();
                } else {
                    this.setTaskViewToFullscreen();
                }
                if (this.selectedSpot) {
                    this.leaveSpot(this.selectedSpot.id);
                    // a real leave: the v-if destroy alone would only pause the step audio
                    this.$store.dispatch('taskaudio/stop');
                }
                this.showTask = false;
            },
            deep: true,
            immediate: true,
        },
        spot: {
            handler(val) {
                if (val) {
                    this.selectedSpot = val;
                }
            },
            immediate: true,
            deep: true,
        },
        showTask(val) {
            if (!val) {
                //clear selection in list to prevent double click bugs
                this.$refs.spotList.clearSelect();
            }
        },
        selectedSpot(val, oldVal) {
            // switching to another spot (or to none) counts as leaving the old one
            if (oldVal && (!val || val.id !== oldVal.id)) {
                this.leaveSpot(oldVal.id);
            }
            // until the footer re-inits the wizard, a stale step would select the previous step's
            // audio of the new spot; not on a mere object swap of the same spot (refresh)
            if (!val || !oldVal || val.id !== oldVal.id) {
                this.wizardStep = 0;
            }
        },
        showAnswerAnimation(val, oldVal) {
            // The details are hidden while the animation runs, so the scroll from the text watcher in
            // HLSpotDetails is a no-op; position the result once the details are visible again.
            // Checked by animation type: spot.trials also stays > 0 after a correct solution.
            if (oldVal && !val) {
                this.$nextTick(() => {
                    if (this.answerResultType === CONFIG.USER_ANSWER_RESULT_TRY_AGAIN) {
                        this.scrollToBottom('hlSpotDetails'); // keep the input visible for the next try
                    } else {
                        this.scrollToTopOfView('hlSpotDetails');
                    }
                });
            }
        },
        coarseDialogPending() {
            this.maybeShowCoarseDialog();
        },
        // a pending coarse dialog waits until the shared GPS dialog and the overlays are closed
        gpsDialog(val) {
            if (!val) {
                this.maybeShowCoarseDialog();
            }
        },
        qrDialogOverlay(val) {
            if (!val) {
                this.maybeShowCoarseDialog();
            }
        },
        passwordDialogOverlay(val) {
            if (!val) {
                this.maybeShowCoarseDialog();
            }
        },
    },
    computed: {
        ...mapGetters('taskpage', ['singleView', 'hideFooter', 'autoCheckin', 'spot']),

        // the details are only v-show-hidden during the answer animation, but the footer already
        // switched the step; without this the feedback audio would start under the animation
        taskPanelActive() {
            return this.pageActive && !this.showAnswerAnimation;
        },

        showmap() {
            let show = true;
            // Checking if showmap is present in the route params and setting this.showmap accordingly
            if (Object.prototype.hasOwnProperty.call(this.$route.params,'showmap')) {
                if (this.$route.params.showmap === 'map') {
                    show = true;
                } else if (this.$route.params.showmap === 'nomap') {
                    show = false;
                } else {
                    // If showmap has any other value, set it based on the computed property shouldShowMap
                    show = this.shouldShowMap;
                }
            } else {
                // If showmap is not present in the route params, set it based on the computed property shouldShowMap
                show = this.shouldShowMap;
            }
            return show;
        },
        spotTitle() {
            return this.getSpotTitle(this.selectedSpot);
        },
        showNext() {
            if (
                !this._.isEmpty(this.selectedSpot) &&
                this._.isArray(this.allActiveVisibleSpots)
            ) {
                return (
                    this._.findIndex(this.allActiveVisibleSpots, [
                        'id',
                        this.selectedSpot.id,
                    ]) <
                    this.allActiveVisibleSpots.length - 1
                );
            }
            return false;
        },
        shouldPlaceActionsInTitle() {
            const refreshDisabled = this.parseBoolean(CONFIG.DISABLE_TASK_REFRESH_BTN, false);
            const tilesDisabled = this.parseBoolean(CONFIG.DISABLE_TASK_TILE_BTN, false);
            const sortDisabled = this.parseBoolean(CONFIG.DISABLE_TASK_SORT_BTN, false);
            const moreDisabled = this.parseBoolean(CONFIG.DISABLE_TASK_MORE_BTN, false);
            const qrDisabled = moreDisabled || !this.hasQrCheckin;
            const pwDisabled = moreDisabled || !this.hasPwCheckin;
            const disabledCount = [refreshDisabled, tilesDisabled, sortDisabled, qrDisabled, pwDisabled].filter(Boolean).length;
            return disabledCount >= 2;
        },
        showPrev() {
            if (
                !this._.isEmpty(this.selectedSpot) &&
                this._.isArray(this.allActiveVisibleSpots)
            ) {
                return (
                    this._.findIndex(this.allActiveVisibleSpots, [
                        'id',
                        this.selectedSpot.id,
                    ]) > 0
                );
            }
            return false;
        },
        hasTaskInput() {
            return !this._.isEmpty(this.taskInput);
        },
        hasCantPlayAgainError() {
            return (
                !this._.isEmpty(this.error) && this.error.error == 'cant_play_again'
            );
        },
    },
    methods: {

        onCardLayoutChanged(val) {
            if (!val) {
                this.centerSpotInMap(this.selectedSpot); // Center last selected Spot
                this.onCloseTask();
            }
        },
        onCloseTask() {
            if (this.selectedSpot) {
                this.leaveSpot(this.selectedSpot.id);
            }
            EventBus.$emit(CONFIG.EVENT_BUS_CLOSE_SPOT);
        },
        onSelectTaskFromMap(spotId) {
            this.scrollToSpotInList(spotId);
            this.onSelectTask(spotId);
        },
        onSelectTaskFromList(spotId) {
            this.getSpotById(spotId).then((spot) => {
                if (this.centerSpotImmediatly || !this.showTask) {
                    this.centerSpotInMap(spot);
                }
                this.onSelectTask(spotId);
            });
        },
        onSelectTask(spotId) {
            if (spotId) {
                this.getSpotById(spotId).then((val) => {
                    if( this.canOpenTaskView(val) ){
                        this.selectedSpot = val;
                        this.showTask = true;
                        this.taskInput = null;
                        this.taskInputServerData = null;
                        this.error = null;
                    }
                    else{
                        this.$refs.spotList.clearSelect();
                    }
                });
            }
        },

        // Shows the coarse-location hint once per page load. It shares the HLConfirm with the other
        // GPS dialogs, so it never replaces an open one or sits on top of the QR/password overlays.
        maybeShowCoarseDialog() {
            if (!this.coarseDialogPending || this.gpsDialog || this.qrDialogOverlay || this.passwordDialogOverlay) {
                return;
            }
            this.$store.commit('geolocation/SET_COARSE_DIALOG_SHOWN');
            this.showGpsCoarseDialog();
        },

        // without a position the "too far away" message would mislead; explain the GPS state instead
        notifyNotInRange(spot){
            if (this.isGpsDenied || spot.distanceToCurrentPosition == null) {
                this.showGpsProblemDialog();
            } else {
                this.showNotInRangeDialog(spot);
            }
        },

        canOpenTaskView(spot){
            // console.log('spot: ', spot);
            if (this.spotAlreadyTouched(spot)) {
                // console.log('spotAlreadyTouched');
                return true;
            }
            else if (this.eventIsInactive) {
                // console.log('eventIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskEventNotActive', { event: this.$t('event') }));
                return false;
            }
            else if (this.isAutoCheckinSpot(spot)) {
                // console.log('isAutoCheckinTask');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('task_auto_checkin_warning'));
            }
            else if (this.spotHasDescription(spot)) {
                // console.log('spotHasDescription');
                return true;
            }
            else if (this.spotIsInactive(spot)) {
                // console.log('spotIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskInactive'));
                return false;
            }
            else if (this.spotIsCheckedIn(spot)) {
                // console.log('spotIsCheckedIn');
                return true;
            }
            else if (this.spotMustBeInRange(spot, this.settingsGpsDisabled) && !this.spotIsInRange(spot)) {
                // console.log('spotMustBeInRange');
                this.notifyNotInRange(spot);
                return false;
            }
            else if (this.spotHasAnswerTimeLimit(spot)) {
                // console.log('spotHasAnswerTimeLimit');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskTimeWarning', { time: spot.answer_time_limit, task: this.$t('menuTask') }));
                // EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('task_answer_time_limit_warning', {d: (spot.answer_time_limit)}));
                return true;
            }
            else {
                return true;
            }
        },

        onOpenTask(spot) {
            this.showAnswerAnimation = false;
            this.selectedSpot = spot;
        },
        onChangeSortOrder(order) {
            this.sortOrder = order;
        },
        onSwitchListMode(mode) {
            this.mode = mode;
        },
        onRefreshSpotList() {
            this.showTask = false;
            this.selectedSpot = null;
            this.onCloseTask()
            this.reloadSpotList();
        },
        onQrAction() {
            // Open QR dialog overlay
            this.qrDialogOverlay = true;
        },
        onPwAction() {
            // Open Password dialog overlay
            this.passwordDialogOverlay = true;
        },
        onGoNext() {
            this.$refs.spotList.next();
        },
        onGoPrev() {
            this.$refs.spotList.prev();
        },
        onChangedWizardStep(step) {
            this.wizardStep = step;
        },
        onCheckin() {
            if (this.selectedSpot && this.selectedEvent && this.canCheckin()) {
                this.performCheckinAndGoToWizardStep();
            }
        },

        canCheckin(){
            // console.log('this.selectedSpot: ', this.selectedSpot);
            if (this.spotIsCheckedIn(this.selectedSpot)) {
                // console.log('spotIsCheckedIn');
                return true;
            }
            else if (this.eventIsInactive) {
                // console.log('eventIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskEventNotActive', { event: this.$t('event') }));
                return false;
            }
            else if (this.spotIsInactive(this.selectedSpot)) {
                // console.log('spotIsInactive');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('taskInactive'));
                return false;
            }
            else if (this.spotMustBeInRange(this.selectedSpot, this.settingsGpsDisabled) && !this.spotIsInRange(this.selectedSpot)) {
                // console.log('spotMustBeInRange');
                this.notifyNotInRange(this.selectedSpot);
                return false;
            }
            else if (this.spotHasAnswerTimeLimit(this.selectedSpot)) {
                // console.log('spotHasAnswerTimeLimit');
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('task_answer_time_limit_warning', {d: (this.selectedSpot.answer_time_limit)}));
                return true;
            }
            else{
                // console.log('defaultCheckIn');
                return true;
            }
        },

        setMapViewToFullscreen() {
            this.hideTaskList = true;
        },
        setTaskViewToFullscreen() {
            this.hideTaskList = false;
        },
        centerSpotInMap(spotId) {
            if (this.showmap && this.$refs.map) {
                this.$refs.map.centerSpot(spotId);
            }
        },
        scrollToSpotInList(spotId) {
            if (this.$refs.spotList) {
                this.$refs.spotList.centerSpot(spotId);
            }
        },

        updateSpotOnMap(spot) {
            if (this.showmap && this.$refs.map) {
                this.$refs.map.updateSpot(spot);
            }
        },

        showAnswerResults(spot) {
            this.answerResultType = this.isInfoSpot(spot)
                ? CONFIG.USER_INFO_SPOT_PASSED
                : this.spotIsSolved(spot)
                    ? CONFIG.USER_ANSWER_RESULT_SOLVED
                    : this.hasTrialsLeft(spot)
                        ? CONFIG.USER_ANSWER_RESULT_TRY_AGAIN
                        : CONFIG.USER_ANSWER_RESULT_FAILED;
            this.showAnswerAnimation = true;
        },
        reloadSpotListOnCantPlayAgainAndEnabledServerSync(force = false) {
            this.$nextTick(() => {
                if (this.hasCantPlayAgainError && (force || this.serverSync)) {
                    this.reloadSpotList();
                }
            });
        },
        reloadSpotListOnCantPlayAgain() {
            this.reloadSpotListOnCantPlayAgainAndEnabledServerSync(true);
        },
        reloadSpotList() {
            this.getSpotsByEventId(this.selectedEventId).then(() => {
                this.getPlayerinfo();
                //FORCE to close active spot and open it again
                this.refreshView();
            });
        },
        refreshView() {
            if (!this._.isEmpty(this.selectedSpot) && this.showTask) {
                this.showTask = false;
                this.$nextTick(() => {
                    this.onSelectTask(this.selectedSpot.id);
                });
            }
        },
        invalidateView() {
            this.invalidateCounter++;
        },

        notifyMasterSlaveTeamPlayersForSolve(spot) {
            try {
                if (this.isTeamLeaderInMasterSlaveMode) {
                    this.$websocket.broadcast.solve(spot.id);
                }
            } catch (ex) {
                console.log(ex);
            }
        },

        notifyMasterSlaveTeamPlayersForCheckin(spot, openInPopup = false) {
            try {
                if (this.isTeamLeaderInMasterSlaveMode && spot) {
                    this.$websocket.broadcast.checkin(spot.id, openInPopup);
                }
            } catch (ex) {
                console.log(ex);
            }
        },

        onSolve(sendEmptySolution, solution) {
            // console.log('onSolve solution: ', solution);
            if (
                this.selectedSpot &&
                this.selectedEvent &&
                this.isTeamLeaderInMasterSlaveModeOrIsSingleMode
            ) {

                if( !this._.isEmpty(solution) ){
                    this.onTaskInput(solution, this.getServerData(solution));
                }

                sendEmptySolution = this._.isBoolean(sendEmptySolution)
                    ? sendEmptySolution
                    : false;
                if (sendEmptySolution) {
                    if (!this.hasTaskInput) {
                        this.onTaskInput('', this.getServerData(''));
                        //Send offset of 1 sec if countdown ended to prevent try again animation instead of failed
                        this.taskInputServerData.date = this.getCurrentDateTimeWithOffset(
                            1,
                            this.selectedEvent.timezone,
                        );
                    }
                }

                const spot = this.selectedSpot;
                this.solveSpot(
                    spot.id,
                    this.selectedEventId,
                    this.selectedEvent.timezone,
                    this.taskInputServerData,
                    (ex) => {
                        this.onError(ex);
                        //this.reloadSpotListOnCantPlayAgainAndEnabledServerSync();
                    },
                    () => {
                        //Additional Success callback
                        this.notifyMasterSlaveTeamPlayersForSolve(spot); //send broadcast to inform other users of same team
                    },
                )
                    .then((solvedSpot) => {
                        EventBus.$emit(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, spot);
                        this.getPlayerinfo(); // Refresh points //TODO replace by offline berechnung
                        this.showAnswerResults(solvedSpot);
                        if (this.serverSync) {
                            // Refresh spot list in sync mode
                            //this.getSpotsByEventId({eventId:this.selectedEventId,silent:true});
                        }
                    })
                    .catch((err) => {
                        // no result animation without a server answer; the player sees the error
                        // notification and keeps the input for another try
                        console.log(err);
                        this.onError(err);
                        if (this.isInfoSpot(spot) && !isCantPlayAgainError(err)) {
                            this.undoInfoSpotCheckin(spot);
                        }
                    });
            }
        },
        /**
         * Takes the local checkin of an info spot back after its solve got no server answer, so
         * the spot stays playable. In the auto-checkin overlay the spot is parked until the next
         * successful event refresh and the overlay closed (it would be re-checked-in at once
         * otherwise); elsewhere the wizard returns to the checkin step.
         */
        undoInfoSpotCheckin(spot) {
            const inOverlay = this.autoCheckin;
            if (inOverlay) {
                this.suppressAutoCheckin(spot.id);
                this.onCloseTask();
            }
            this.resetCheckinSpot(spot.id).then(() => {
                if (!inOverlay && this.$refs.spotDetailsFooter) {
                    this.$refs.spotDetailsFooter.goToWizardStep(CONFIG.TASK_STEPPER_CHECKIN);
                }
            });
        },
        onTaskInput(val, serverData) {
            this.taskInput = val;
            this.taskInputServerData = serverData;
        },

        onError(val) {
            if (val && val.error) {
                this.error = val;
            }
            if (
                val &&
                !this.hasCantPlayAgainError &&
                (val.isAxiosError ||
                    this._.isEmpty(val.response) ||
                    !val.response.status)
            ) {
                EventBusNotification.$emit(CONFIG.EVENT_BUS_NOTIFICATION_SHOW, this.$t('msgTechnicalError'));
            }
            console.warn(val);
        },
        onHelp(val) {
            let searchedSpot = this.getSpotByPassword(val);
            //let foundSpot=!this._.isEmpty(searchedSpot);

            if (this.$refs.spotDetailsFooter) {
                this.$refs.spotDetailsFooter.setHelpSystemState(searchedSpot);
            }
        },
        onRequestHint(hintId) {
            // console.log("hintId", hintId);
            if (
                this.selectedSpot &&
                this.selectedEvent &&
                this.isTeamLeaderInMasterSlaveModeOrIsSingleMode &&
                !this.isLoadingHintRequest
            ) {
                this.taskInputServerData = {};
                this.taskInputServerData.date = this.getCurrentDateTimeWithOffset(
                    1,
                    this.selectedEvent.timezone,
                );

                this.requestHint(
                    hintId,
                    this.selectedSpot.id,
                    this.selectedEventId,
                    this.selectedEvent.timezone,
                    this.taskInputServerData,
                    (ex) => {
                        this.onError(ex);
                        //this.reloadSpotListOnCantPlayAgainAndEnabledServerSync();
                    },
                    () => {
                        //Additional Success callback
                        this.notifyMasterSlaveTeamPlayersForSolve(this.selectedSpot); //send broadcast to inform other users of same team
                    },
                )
                    .then(() => {
                        EventBus.$emit(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, this.selectedSpot);
                        this.getPlayerinfo(); // Refresh points //TODO replace by offline berechnung

                        // TODO notify spot update for hint view
                    })
                    .catch((err) => {
                        console.log(err);
                        this.onError(err);
                    });
            }
        },

        // QR Dialog handlers
        onQrCancel() {
            this.qrDialogOverlay = false;
        },
        onQrSuccess(qrCode) {
            // console.log('QR Code scanned:', qrCode);

            let spot = this.getSpotByQrCode(qrCode)
            if (spot) {
                this.showQrSuccessAnimation().then(() => {
                    this.qrDialogOverlay = false;

                    if (spot) {
                        if (this.isSecretSpot(spot)) {
                            EventBus.$emit(CONFIG.EVENT_BUS_OPEN_SPOT,spot);
                        } else {
                            this.showTask = true;
                            this.selectedSpot = spot;
                            this.performCheckinAndGoToWizardStep();
                        }
                    }
                });
            } else {
                this.showQrFailAnimation().then(() => {
                    this.qrDialogOverlay = false;
                });
            }
        },
        showQrSuccessAnimation() {
            return new Promise((resolve) => {
                this.qrAnimationClass = 'bounce';
                this.overlayColor = CONFIG.getGreen();
                setTimeout(() => {
                    this.resetQrOverlayAnimation();
                    resolve();
                }, this.animationSuccessDuration);
            });
        },
        showQrFailAnimation() {
            return new Promise((resolve) => {
                this.qrAnimationClass = 'shake-horizontal';
                this.overlayColor = CONFIG.getRed();
                setTimeout(() => {
                    this.resetQrOverlayAnimation();
                    resolve();
                }, this.animationFailedDuration);
            });
        },
        resetQrOverlayAnimation() {
            this.qrAnimationClass = '';
            this.overlayColor = "#333";
        },

        // Password Dialog handlers
        onPasswordCancel() {
            this.passwordDialogOverlay = false;
        },
        onPasswordSuccess(spot) {

            this.showPasswordSuccessAnimation().then(() => {
                this.passwordDialogOverlay = false;

                if (spot) {
                    if (this.isSecretSpot(spot)) {
                        EventBus.$emit(CONFIG.EVENT_BUS_OPEN_SPOT,spot);
                    } else {
                        this.showTask = true;
                        this.selectedSpot = spot;
                        this.performCheckinAndGoToWizardStep();
                    }
                }
            });
        },
        async performCheckinAndGoToWizardStep() {
            try {
                await this.performCheckin();
                // Wait for next tick to ensure the footer component is ready
                this.$nextTick(() => {
                    if (this.$refs.spotDetailsFooter) {
                        this.$refs.spotDetailsFooter.goToWizardStep(CONFIG.TASK_STEPPER_TASK);
                    }
                });
            } catch (error) {
                console.log('Checkin failed:', error);
            }
        },
        performCheckin() {
            return new Promise((resolve, reject) => {

                if (this.selectedSpot && this.selectedEvent && this.canCheckin()) {
                    //Allow checkin in master/slave mode. But do not send
                    if (this.isSlaveInMasterSlaveMode) {
                        this.checkinSpot(
                            this.selectedSpot.id,
                            this.selectedEventId,
                            this.selectedEvent.timezone,
                            true,
                        );
                        resolve();
                        return;
                    }

                    // info spots do not need checkin, instead solve directly after 'checkin'
                    let doNotSend = this.isInfoSpot(this.selectedSpot);
                    this.checkinSpot(
                        this.selectedSpot.id,
                        this.selectedEventId,
                        this.selectedEvent.timezone,
                        doNotSend,
                        (ex) => {
                            this.onError(ex);
                            reject(ex);
                        },
                    ).then((spot) => {
                            if (this.isInfoSpot(spot)) {
                                this.onSolve(true);
                            } else {
                                this.notifyMasterSlaveTeamPlayersForCheckin(spot);
                            }
                            resolve(spot);
                        })
                        .catch((ex) => {
                            console.log('check in error');
                            console.log(ex);
                            this.onError(ex);
                            reject(ex);
                        });
                } else {
                    reject(new Error('Cannot checkin'));
                }
            });
        },
        showPasswordSuccessAnimation() {
            return new Promise((resolve) => {
                this.passwordAnimationClass = 'bounce';
                this.overlayColor = CONFIG.getGreen();
                setTimeout(() => {
                    this.resetPasswordOverlayAnimation();
                    resolve();
                }, this.animationSuccessDuration);
            });
        },
        resetPasswordOverlayAnimation() {
            this.passwordAnimationClass = '';
            this.overlayColor = "#333";
        },
        registerEventListener() {
            EventBus.$on(CONFIG.EVENT_BUS_REFRESH_SPOT, () => {
                this.$nextTick(() => {
                    this.refreshView();
                });
            });
            EventBus.$on(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP, (spot) => {
                this.updateSpotOnMap(spot);
            });
        },
        unregisterEventListener() {
            EventBus.$off(CONFIG.EVENT_BUS_REFRESH_SPOT);
            EventBus.$off(CONFIG.EVENT_BUS_UPDATE_SPOT_ON_MAP);
        },
    },

    mounted() {
        this.registerEventListener();
    },
    activated() {
        this.pageActive = true;
    },
    deactivated() {
        this.pageActive = false;
    },
    beforeDestroy() {
        // do not use this because overlays of type TasksPage will remove the task refresh
        //this.unregisterEventListener();
    },
};
</script>

<style lang="scss" scoped>
.tasks-page-wrapper {
    height: 100%;
    width: 100%;
    display: flex;
    flex-direction: column;
}

.menu-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
}

.menu-title-text {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.menu-title-actions {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
}
.bounce {
    animation-duration: 2s;
    animation-iteration-count: infinite;
    animation-name: bounce;
    animation-timing-function: ease;
}

@keyframes bounce {
    0%   { transform: translateY(0); }
    30%  { transform: translateY(-100px); }
    50%  { transform: translateY(0); }
    100% { transform: translateY(0); }
}

.shake-horizontal {
    -webkit-animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
    animation: shake-horizontal 0.8s cubic-bezier(0.455, 0.030, 0.515, 0.955) both;
}

@-webkit-keyframes shake-horizontal {
    0%, 100% {
        -webkit-transform: translateX(0);
        transform: translateX(0);
    }
    10%, 30%, 50%, 70% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
    }
    20%, 40%, 60% {
        -webkit-transform: translateX(10px);
        transform: translateX(10px);
    }
    80% {
        -webkit-transform: translateX(8px);
        transform: translateX(8px);
    }
    90% {
        -webkit-transform: translateX(-8px);
        transform: translateX(-8px);
    }
}

@keyframes shake-horizontal {
    0%, 100% {
        -webkit-transform: translateX(0);
        transform: translateX(0);
    }
    10%, 30%, 50%, 70% {
        -webkit-transform: translateX(-10px);
        transform: translateX(-10px);
    }
    20%, 40%, 60% {
        -webkit-transform: translateX(10px);
        transform: translateX(10px);
    }
    80% {
        -webkit-transform: translateX(8px);
        transform: translateX(8px);
    }
    90% {
        -webkit-transform: translateX(-8px);
        transform: translateX(-8px);
    }
}
</style>
